import { useEffect, useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Clock,
  MessageCircle,
  CheckCircle2,
  ArrowRight,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Loader,
} from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { contactApi } from "../lib";
import { showSuccess, showError } from "../lib/toast";
import SEO from "../components/SEO";

gsap.registerPlugin(ScrollTrigger);

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await contactApi.submitContact(formData);
      if (res.success) {
        setIsSubmitted(true);
        showSuccess("Message sent! We'll get back to you soon.");
        setFormData({ name: "", email: "", subject: "", message: "" });
      }
    } catch (err: any) {
      showError(err.message || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".page-header",
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, ease: "expo.out" },
      );

      gsap.fromTo(
        ".contact-card",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.08,
          ease: "expo.out",
          scrollTrigger: {
            trigger: ".contact-grid",
            start: "top 80%",
          },
        },
      );

      gsap.fromTo(
        ".form-container",
        { opacity: 0, x: 30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.5,
          ease: "expo.out",
          scrollTrigger: {
            trigger: ".form-container",
            start: "top 80%",
          },
        },
      );

      gsap.fromTo(
        ".faq-item",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.08,
          ease: "expo.out",
          scrollTrigger: {
            trigger: ".faq-section",
            start: "top 80%",
          },
        },
      );
    });

    return () => ctx.revert();
  }, []);

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      content: "ndzelenromarick@gmail.com",
      description: "We'll respond within 24 hours",
      link: "mailto:ndzelenromarick@gmail.com",
    },
    {
      icon: Phone,
      title: "Call Us",
      content: "+(237) 678-196-930",
      description: "Mon-Fri, 9am-6pm EST",
      link: "tel:+237678196930",
    },
    {
      icon: MapPin,
      title: "Visit Us",
      content: "Bamenda Cameroon",
      description: "ENS street, Bambili",
      link: "#",
    },
    {
      icon: Clock,
      title: "Working Hours",
      content: "Monday - Friday",
      description: "9:00 AM - 6:00 PM EST",
      link: "#",
    },
  ];

  const socialLinks = [
    {
      icon: Facebook,
      href: "https://facebook.com/romarick10",
      label: "Facebook",
      color: "bg-[#1877f2]",
    },
    {
      icon: Twitter,
      href: "https://twitter.com/bongntehndzelen",
      label: "Twitter",
      color: "bg-[#1da1f2]",
    },
    {
      icon: Instagram,
      href: "https://instagram.com",
      label: "Instagram",
      color: "bg-gradient-to-br from-[#f58529] to-[#dd2a7b]",
    },
    {
      icon: Linkedin,
      href: "https://linkedin.com",
      label: "LinkedIn",
      color: "bg-[#0a66c2]",
    },
  ];

  const faqs = [
    {
      question: "How do I enroll in a course?",
      answer:
        "Simply browse our courses, select the one you're interested in, and click \"Enroll Now\". You'll be guided through the payment process and gain immediate access to all course materials.",
    },
    // {
    //   question: "Can I get a refund if I'm not satisfied?",
    //   answer:
    //     "Yes! We offer a 30-day money-back guarantee on all courses. If you're not completely satisfied with your purchase, contact our support team for a full refund, no questions asked.",
    // },
    {
      question: "How does the mentorship program work?",
      answer:
        "Browse mentor profiles, book a session that fits your schedule, and connect via video call. You can message your mentor anytime between sessions for ongoing support and guidance.",
    },
    {
      question: "Are the courses self-paced?",
      answer:
        "Yes, all our courses are self-paced. You can learn at your own speed and access the content anytime, anywhere, on any device.",
    },
    {
      question: "Do I get a certificate upon completion?",
      answer:
        "Absolutely! Every course comes with a certificate of completion that you can share on your LinkedIn profile, resume, or portfolio.",
    },
  ];

  return (
    <>
      <SEO
        title="Contact LearnHub Institute - Get in Touch With Our Team"
        description="Have questions about our courses, mentorship, or platform? Contact LearnHub Institute today. Our support team is ready to help you on your learning journey."
        keywords="contact learnhub, customer support, help desk, technical support, mentorship questions, course inquiries"
      />
      <main className="relative min-h-screen pt-20 pb-16 bg-white">
      {/* Hero Section */}
      <div className="mb-12 section-container">
        <div className="max-w-3xl mx-auto text-center page-header">
          <span className="inline-block px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full mb-4">
            Get in Touch
          </span>
          <h1 className="mb-3 text-2xl font-bold text-black lg:text-3xl xl:text-4xl">
            We'd Love to <span className="text-gradient">Hear From You</span>
          </h1>
          <p className="max-w-2xl mx-auto text-sm leading-relaxed lg:text-base text-gray-medium">
            Have a question, feedback, or just want to say hello? Our team is
            here to help you on your learning journey.
          </p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="mb-12 section-container">
        <div className="grid gap-4 contact-grid sm:grid-cols-2 lg:grid-cols-4">
          {contactInfo.map((info, index) => (
            <a
              key={index}
              href={info.link}
              className="p-5 transition-all duration-300 bg-white contact-card rounded-xl shadow-card hover:shadow-card-hover hover:-translate-y-1 group"
            >
              <div className="flex items-center justify-center w-10 h-10 mb-3 transition-all duration-300 rounded-lg bg-primary/10 group-hover:bg-primary group-hover:scale-110">
                <info.icon className="w-4 h-4 transition-colors text-primary group-hover:text-white" />
              </div>
              <h3 className="mb-1 text-sm font-semibold text-black">
                {info.title}
              </h3>
              <p className="text-xs text-black font-medium mb-0.5">
                {info.content}
              </p>
              <p className="text-xs text-gray-medium">{info.description}</p>
            </a>
          ))}
        </div>
      </div>

      {/* Contact Form & Map */}
      <div className="mb-16 section-container">
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Form */}
          <div className="p-6 form-container bg-gray-50 rounded-xl lg:p-8">
            <h2 className="mb-1 text-lg font-bold text-black">
              Send us a Message
            </h2>
            <p className="mb-6 text-xs text-gray-medium">
              Fill out the form below and we'll get back to you as soon as
              possible.
            </p>

            {isSubmitted ? (
              <div className="py-10 text-center">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="mb-1 text-base font-bold text-black">
                  Message Sent!
                </h3>
                <p className="text-xs text-gray-medium">
                  Thank you for reaching out. We'll get back to you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-black mb-1.5">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 text-sm transition-all bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-black mb-1.5">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 text-sm transition-all bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Subject
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 text-sm transition-all bg-white border border-gray-200 rounded-lg appearance-none focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="courses">Course Questions</option>
                    <option value="mentorship">Mentorship</option>
                    <option value="technical">Technical Support</option>
                    <option value="billing">Billing & Payments</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-3 py-2 text-sm transition-all bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    placeholder="How can we help you?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2 group disabled:opacity-70"
                >
                  {loading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Send Message
                      <Send className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Map & Social */}
          <div className="space-y-5">
            {/* Map Placeholder */}
            <div className="relative flex items-center justify-center h-64 overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
              <div className="absolute inset-0 opacity-20">
                <svg className="w-full h-full" viewBox="0 0 400 300">
                  <path
                    d="M0,150 Q100,100 200,150 T400,150"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-primary"
                  />
                  <circle
                    cx="200"
                    cy="150"
                    r="30"
                    className="fill-primary/30"
                  />
                  <circle
                    cx="200"
                    cy="150"
                    r="15"
                    className="fill-primary/60"
                  />
                  <circle cx="200" cy="150" r="5" className="fill-primary" />
                </svg>
              </div>
              <div className="relative z-10 text-center">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 rounded-full bg-primary/20">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <p className="text-sm font-semibold text-black">
                  123 Learning Street
                </p>
                <p className="text-xs text-gray-medium">
                  Education City, EC 10001
                </p>
              </div>
            </div>

            {/* Social Links */}
            <div className="p-5 bg-white border border-gray-100 rounded-xl shadow-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-primary/10">
                  <MessageCircle className="w-3 h-3 text-primary" />
                </div>
                <h3 className="text-sm font-semibold text-black">
                  Connect With Us
                </h3>
              </div>
              <div className="flex gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className={`w-9 h-9 ${social.color} rounded-lg flex items-center justify-center text-white hover:scale-105 transition-transform duration-300`}
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Support */}
            <div className="p-5 text-white bg-gradient-to-r from-primary to-orange-500 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="w-4 h-4" />
                <h3 className="text-sm font-semibold">Need Quick Support?</h3>
              </div>
              <p className="mb-3 text-xs text-white/80">
                Our support team is available 24/7 to help you with any
                questions.
              </p>
              <a
                href="#"
                className="inline-flex items-center gap-1.5 text-white text-xs font-medium hover:underline"
              >
                Start Live Chat
                <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="section-container">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full mb-3">
            <HelpCircle className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-medium text-primary">FAQ</span>
          </div>
          <h2 className="mb-2 text-xl font-bold text-black lg:text-2xl">
            Frequently Asked Questions
          </h2>
          <p className="max-w-2xl mx-auto text-sm text-gray-medium">
            Find quick answers to common questions
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3 faq-section">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="overflow-hidden transition-shadow bg-white border border-gray-200 faq-item rounded-xl hover:shadow-md"
            >
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="flex items-center justify-between w-full p-4 text-left transition-colors hover:bg-gray-50"
              >
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-primary text-[10px] font-bold">
                      Q
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-black">
                    {faq.question}
                  </span>
                </div>
                {openFaq === index ? (
                  <ChevronUp className="flex-shrink-0 w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="flex-shrink-0 w-4 h-4 text-gray-400" />
                )}
              </button>
              {openFaq === index && (
                <div className="px-4 pb-4 pl-12">
                  <p className="text-sm leading-relaxed text-gray-600">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-medium">
            Can't find what you're looking for?{" "}
            <a
              href="mailto:info@learnhub.com"
              className="font-medium text-primary hover:underline"
            >
              Contact our support team
            </a>
          </p>
        </div>
      </div>

      <style>{`
        .text-gradient {
          background: linear-gradient(135deg, #E85D04 0%, #F48C06 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
      </main>
      </>
  );
};

export default ContactPage;
